import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type {
  ItemPacking,
  ItemType,
  Order,
  VehicleTypes,
} from '@hero/schema';
import type { OrderUpdateAPI } from '@hero/api';
import { getObjectId } from '@hero/schema';
import { useClientApi, useClientUpdateApi, useOrderUpdateApi } from '@hero/api-hooks';
import { convertWeight } from '@hero/core';
import { useIsLogistiConnected } from '@hero/hooks-shared';
import {
  getClientIdFromOrder,
  loadMatchesClientSettings,
  mergeClientSettingsWithLoad,
  projectLoadIntoClientSettings,
  type LoadDefaultsSource,
} from '@hero/web-hooks';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../../ui/Sheet/Sheet';
import { Button } from '../../../ui/Button/Button';
import { Checkbox } from '../../../ui/Checkbox/Checkbox';
import Spinner from '../../../app/Spinner/Spinner';
import { LoadDetailsFields, type LoadDetailsFieldsValue } from '../Load/LoadDetailsFields';
import { PickupDateModal } from '../PickupDate/PickupDateModal';

export interface OrderLoadEditDrawerProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Single-shot edit drawer for an order's load details.
 *
 * One PATCH covers everything the user can touch from this surface —
 * pickup date, accepted vehicle types, item description / type /
 * packing / quantity / weight / declared value, and temperature.
 * Vehicle *count* is intentionally not editable: a single Order
 * always corresponds to a single trip, and "I need 3 trucks" is
 * modelled as a job that splits into 3 sibling orders at create
 * time. Editing it here would require re-shaping the parent job,
 * which doesn't belong on this surface.
 *
 * When the order has a CRM client, a footer checkbox optionally
 * persists the load fields (not pickup dates) as that client's
 * defaults. Client fetch is non-blocking — only the checkbox shows
 * a loading state.
 */
export function OrderLoadEditDrawer({ order, isOpen, onClose }: OrderLoadEditDrawerProps) {
  const { t } = useTranslation(['m-order', 'c-main', 'c-price']);
  const updateOrder = useOrderUpdateApi();
  const updateClient = useClientUpdateApi();
  const [pickupModalOpen, setPickupModalOpen] = useState(false);
  const [saveAsDefault, setSaveAsDefault] = useState(false);

  const clientId = useMemo(() => getClientIdFromOrder(order), [order]);
  const { data: client, isLoading: isClientLoading, isFetching: isClientFetching } = useClientApi(
    isOpen && clientId ? clientId : undefined,
  );
  const clientSettingsLoading = Boolean(clientId) && (isClientLoading || isClientFetching) && !client;

  const initial = useMemo<LoadDetailsFieldsValue>(() => buildInitial(order), [order]);
  const [form, setForm] = useState<LoadDetailsFieldsValue>(initial);

  // Re-seed when the order or open state changes — guards against
  // showing stale data when the user closes the drawer mid-edit and
  // re-opens it on a different order.
  useEffect(() => {
    if (isOpen) setForm(initial);
  }, [isOpen, initial]);

  const loadSource = useMemo<LoadDefaultsSource>(() => formToLoadSource(form), [form]);

  // Seed checkbox when the drawer opens / client arrives: checked when
  // the order's load already matches client defaults. Do not re-bind on
  // every keystroke so the user can opt in while editing.
  useEffect(() => {
    if (!isOpen) {
      setSaveAsDefault(false);
      return;
    }
    if (!clientId || !client) return;
    setSaveAsDefault(loadMatchesClientSettings(formToLoadSource(initial), client.settings));
  }, [isOpen, clientId, client, initial]);

  const handleChange = (patch: Partial<LoadDetailsFieldsValue>) =>
    setForm(prev => ({ ...prev, ...patch }));

  const handleSave = async () => {
    if (!order) return;
    try {
      const payload: OrderUpdateAPI['req'] = {
        _id: order._id,
      };

      // Pickup window — only patch when both ends are present.
      if (form.pickupFrom && form.pickupTo) {
        payload.pickupDate = { from: form.pickupFrom, to: form.pickupTo };
      }

      payload.vehicleTypes = form.vehicleTypes as OrderUpdateAPI['req']['vehicleTypes'];

      if (form.temperatureType !== initial.temperatureType) {
        payload.temperature = form.temperatureType
          ? { types: [form.temperatureType as 'dry' | 'chilled' | 'frozen' | 'hot'] }
          : { types: ['dry'] };
      }

      const existingItem = order.load?.items?.[0];
      payload.items = [
        {
          ...(existingItem?._id ? { _id: existingItem._id } : {}),
          name: form.itemDescription || existingItem?.name,
          type: form.itemType ?? existingItem?.type,
          packing: form.packing ?? existingItem?.packing,
          quantity: form.quantity > 0 ? form.quantity : existingItem?.quantity ?? 1,
          weights:
            form.weight && form.weight > 0
              ? { value: form.weight, type: 'ton' as const }
              : existingItem?.weights,
          cost: form.itemCost ?? existingItem?.cost,
        },
      ] as OrderUpdateAPI['req']['items'];

      await updateOrder.mutateAsync(payload);

      if (saveAsDefault && clientId) {
        const loadPatch = projectLoadIntoClientSettings(loadSource);
        const settings = mergeClientSettingsWithLoad(client?.settings, loadPatch);
        await updateClient.mutateAsync({
          _id: getObjectId(clientId),
          settings,
        });
      }

      toast.success(
        t('m-order:drawer.load_edit.saved_toast', { defaultValue: 'Load details updated' }),
      );
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(message);
    }
  };

  const isSaving = updateOrder.isPending || updateClient.isPending;
  const clientName = client?.name ?? order?.client?.name;

  // Orders created before this rule carry no declared value, so on a
  // Logisti-connected Org this will demand one even when the user came here
  // to change something unrelated. That is the intended trade: the alternative
  // is discovering it at issue time, when the truck is already loaded.
  const isGoodsValueMissing = useIsLogistiConnected() && form.itemCost == null;

  return (
    <>
      <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
        <SheetContent
          side="right"
          className="flex flex-col p-0 bg-white w-[480px] sm:max-w-[480px]">
          <SheetHeader className="border-b px-6 py-4">
            <SheetTitle>
              {t('m-order:drawer.load_edit.title', { defaultValue: 'Edit load details' })}
            </SheetTitle>
            <SheetDescription>
              {t('m-order:drawer.load_edit.description', {
                defaultValue:
                  'Update pickup window, accepted vehicle types and the load itself in one go.',
              })}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-6">
            <LoadDetailsFields
              value={form}
              onChange={handleChange}
              showPickupDate
              onOpenPickupModal={() => setPickupModalOpen(true)}
              weightUnit="ton"
            />
          </div>

          <div className="border-t p-4 flex flex-col gap-3">
            {clientId ? (
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                {clientSettingsLoading ? (
                  <Spinner className="size-4 text-gray-400" />
                ) : (
                  <Checkbox
                    checked={saveAsDefault}
                    onCheckedChange={checked => setSaveAsDefault(checked === true)}
                    disabled={isSaving}
                  />
                )}
                <span>
                  {t('m-order:drawer.load_edit.save_defaults_for_client', {
                    defaultValue: 'Save as default for {{name}}',
                    name: clientName || t('m-order:drawer.load_edit.save_defaults_client_fallback', {
                      defaultValue: 'this client',
                    }),
                  })}
                </span>
              </label>
            ) : null}

            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={onClose}>
                {t('c-main:crud_buttons.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button onClick={handleSave} disabled={isSaving || !order || isGoodsValueMissing}>
                {isSaving
                  ? t('c-main:async_operations.saving', { defaultValue: 'Saving…' })
                  : t('c-main:crud_buttons.save', { defaultValue: 'Save' })}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <PickupDateModal
        isOpen={pickupModalOpen}
        onClose={() => setPickupModalOpen(false)}
        onConfirm={({ dateRange }) => {
          handleChange({ pickupFrom: dateRange.from, pickupTo: dateRange.to });
          setPickupModalOpen(false);
        }}
      />
    </>
  );
}

function formToLoadSource(form: LoadDetailsFieldsValue): LoadDefaultsSource {
  return {
    vehicleTypes: form.vehicleTypes,
    itemDescription: form.itemDescription,
    itemType: form.itemType,
    packing: form.packing,
    quantity: form.quantity,
    weightTon: form.weight,
    itemCost: form.itemCost,
    temperatureType: form.temperatureType,
    podSettings: form.podSettings,
  };
}

function buildInitial(order: Order | null): LoadDetailsFieldsValue {
  if (!order) {
    return {
      pickupFrom: undefined,
      pickupTo: undefined,
      vehicleTypes: [],
      temperatureType: undefined,
      itemDescription: '',
      itemType: undefined,
      packing: undefined,
      quantity: 1,
      weight: undefined,
      itemCost: undefined,
    };
  }
  const item = order.load?.items?.[0];
  // Normalise stored weight (any of `kg | ton | gram`) into tons for
  // the form so the UI is unit-consistent with the load card.
  const weightTon =
    item?.weights && item.weights.value > 0
      ? convertWeight(item.weights, 'ton', { decimals: 3, removeTrailingZeros: true }).value
      : undefined;
  return {
    pickupFrom: order.dates?.pickup?.from
      ? new Date(order.dates.pickup.from as unknown as string)
      : undefined,
    pickupTo: order.dates?.pickup?.to
      ? new Date(order.dates.pickup.to as unknown as string)
      : undefined,
    vehicleTypes: (order.load?.vehicles ?? []) as VehicleTypes[],
    temperatureType: (order.load?.temperature?.types ?? [])[0],
    itemDescription: item?.name ?? '',
    itemType: item?.type as ItemType | undefined,
    packing: item?.packing as ItemPacking | undefined,
    quantity: item?.quantity ?? 1,
    weight: weightTon,
    itemCost: item?.cost as number | undefined,
  };
}
